'use client';

import { useEffect, type RefObject } from 'react';

/**
 * Touch screens: while a panel (the filters slide-over) is open, a TAP outside
 * it only closes the panel. The tap is swallowed, so it never reaches what lies
 * beneath — before, closing the filters by tapping the strip of products beside
 * them opened that product and left the search (founder, iPhone, 2026-11-03).
 *
 * - Taps only: a swipe (more than 10px) is left alone, so the page still scrolls.
 * - Coarse pointers only (phones, tablets): a mouse keeps today's behaviour.
 * - Listens in the capture phase on the panel's root (the widget's shadow root,
 *   or the document on the demo pages), so it runs before anything underneath.
 */
export function useDismissOnOutsideTap(
  ref: RefObject<HTMLElement | null>,
  onDismiss: () => void,
  active: boolean,
) {
  useEffect(() => {
    if (!active || typeof window === 'undefined') return;
    if (!window.matchMedia('(pointer: coarse)').matches) return;
    const panel = ref.current;
    if (!panel) return;
    const root = panel.getRootNode() as Document | ShadowRoot;

    let from: { x: number; y: number } | null = null;
    const onStart = (e: Event) => {
      const t = (e as TouchEvent).touches;
      from = t.length === 1 ? { x: t[0].clientX, y: t[0].clientY } : null;
    };
    const onMove = (e: Event) => {
      const p = (e as TouchEvent).touches[0];
      if (from && p && Math.hypot(p.clientX - from.x, p.clientY - from.y) > 10) from = null;
    };
    const onEnd = (e: Event) => {
      const tap = from;
      from = null;
      if (!tap) return;
      if (e.composedPath().includes(panel)) return;
      e.preventDefault(); // no click: nothing beneath is activated
      e.stopPropagation();
      onDismiss();
    };

    root.addEventListener('touchstart', onStart, { capture: true, passive: true });
    root.addEventListener('touchmove', onMove, { capture: true, passive: true });
    root.addEventListener('touchend', onEnd, { capture: true, passive: false });
    return () => {
      root.removeEventListener('touchstart', onStart, { capture: true });
      root.removeEventListener('touchmove', onMove, { capture: true });
      root.removeEventListener('touchend', onEnd, { capture: true });
    };
  }, [ref, onDismiss, active]);
}
